/**
 * PhotoThumb — ô ảnh NỘI DUNG chuẩn của cả app: khổ 8x10 dọc, ảnh ngang tự
 * xoay thành 10x8, bấm vào mở hộp xem lớn ngay trong trang.
 *
 * CEO chốt 27/09/2026 sau khi soát mọi màn nhân sự: ảnh bằng chứng, ảnh ghi
 * chú, bill chuyển khoản… đang vẽ thành ô 32–80px, nhỏ tới mức phải bấm mới
 * đọc được. Tăng gấp 3 (ô ~80px → cạnh ngắn 240px), khổ 8x10, ảnh ngang 10x8.
 * Chỗ nào đã to sẵn (tường ảnh khách, dải ảnh bảng kiểm 204px…) thì giữ nguyên.
 *
 * Cỡ nằm ở MỘT hằng: muốn đổi cho cả app thì sửa `PHOTO_THUMB_SHORT`, đừng
 * sửa từng màn.
 *
 * Vì sao `object-cover object-top`: bill chuyển khoản là ảnh chụp màn hình
 * điện thoại rất dài — neo mép TRÊN giữ phần đầu bill (trạng thái, số tiền,
 * người nhận) ở cỡ đọc được; ảnh chân dung giữ được mặt. Muốn xem trọn thì bấm.
 *
 * Mặc định coi là ảnh dọc cho tới khi tải xong — ảnh dọc chiếm đa số nên bố
 * cục ít nhảy nhất (cùng cách `GuideThumb` làm, CEO chốt 29/08/2026).
 */

import { useState, type ReactNode } from "react";
import { ImageLightbox } from "@/components/ImageLightbox";
import { VariantImage } from "@/components/VariantImage";
import { isDirectPhotoSrc, photoUrl } from "@/lib/photo-url";
import { cn } from "@/lib/utils";

/** Cạnh ngắn của khung (px). */
export const PHOTO_THUMB_SHORT = 240;
/** Cạnh dài của khung (px) — khổ 8x10. */
export const PHOTO_THUMB_LONG = 300;

export function PhotoThumb({
  src,
  alt = "",
  onOpen,
  openLabel,
  className,
  onError,
  children,
}: {
  /** Con trỏ kho ("note-images/…"), URL đầy đủ, `blob:` xem trước, hoặc đường dẫn tương đối. */
  src: string;
  alt?: string | undefined;
  /** Bấm vào ảnh. Vắng thì ô chỉ để xem, không phải nút. */
  onOpen?: (() => void) | undefined;
  /** Nhãn cho người đọc màn hình. Mặc định "Xem ảnh phóng to". */
  openLabel?: string | undefined;
  /** Lớp thêm cho khung (viền báo lỗi, làm mờ…). */
  className?: string | undefined;
  /**
   * Ảnh địa chỉ đầy đủ tải hỏng (chỗ gọi muốn giấu hẳn ô thay vì để khung vỡ).
   * Con trỏ kho KHÔNG gọi: VariantImage tự thử lại vì biến thể có thể chưa sinh.
   */
  onError?: (() => void) | undefined;
  /**
   * Lớp phủ trên ảnh: nút ✕, chữ "Đang gửi…". Đặt NGOÀI nút mở ảnh — nút lồng
   * trong nút là HTML sai và bấm ✕ sẽ mở luôn ảnh.
   */
  children?: ReactNode;
}) {
  const [landscape, setLandscape] = useState(false);

  const img = {
    alt,
    loading: "lazy" as const,
    decoding: "async" as const,
    className: "absolute inset-0 h-full w-full object-cover object-top",
    onLoad: (e: React.SyntheticEvent<HTMLImageElement>) => {
      const el = e.currentTarget;
      if (el.naturalWidth > 0) setLandscape(el.naturalWidth > el.naturalHeight);
    },
  };
  const picture = isDirectPhotoSrc(src) ? (
    <img src={src} {...img} onError={onError} />
  ) : (
    // Cỡ "md": ô 240–300px trên màn nét cần ~600px, bản "thumb" 256px bị nhoè.
    <VariantImage src={src} variant="md" {...img} />
  );

  return (
    <span
      data-photo-thumb={landscape ? "landscape" : "portrait"}
      className={cn(
        "relative block max-w-full shrink-0 overflow-hidden rounded-md border bg-muted",
        className,
      )}
      style={{
        width: landscape ? PHOTO_THUMB_LONG : PHOTO_THUMB_SHORT,
        aspectRatio: landscape ? "10 / 8" : "8 / 10",
      }}
    >
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label={openLabel ?? "Xem ảnh phóng to"}
          title="Xem ảnh phóng to"
          // Viền nổi bằng outline âm: outline vẽ ĐÈ lên ảnh con, còn ring
          // (box-shadow) nằm dưới ảnh nên không bao giờ thấy.
          className="absolute inset-0 block cursor-zoom-in hover:outline-2 hover:-outline-offset-2 hover:outline-primary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
        >
          {picture}
        </button>
      ) : (
        picture
      )}
      {children}
    </span>
  );
}

/**
 * Nút ✕ góc trên phải của ô ảnh — bỏ / xoá ảnh. Đặt làm `children` hoặc
 * `overlay` của ô.
 *
 * Nút trong suốt 44px ôm hình tròn 32px: điện thoại ép mọi nút cao tối thiểu
 * 44px (globals.css), nút tròn 32px mà bị kéo cao thì méo thành viên thuốc.
 * Nằm TRONG mép ảnh vì khung cắt mọi thứ tràn ra ngoài.
 */
export function PhotoThumbRemove({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean | undefined;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="group/rm absolute right-0 top-0 grid h-11 w-11 place-items-center disabled:opacity-50"
    >
      <span
        aria-hidden
        className="grid h-8 w-8 place-items-center rounded-full border-[1.5px] border-white/90 bg-[rgba(26,26,26,0.72)] text-sm leading-none text-white group-hover/rm:bg-[rgba(26,26,26,0.9)]"
      >
        ✕
      </span>
    </button>
  );
}

/**
 * Một nhóm ảnh nội dung + hộp xem lớn chung: bấm tấm nào mở đúng tấm đó, ← →
 * hoặc vuốt để xem tấm khác trong cùng nhóm.
 *
 * Xếp tràn dòng (`flex-wrap`), mỗi ô tự co về bề ngang khung chứa khi khung
 * hẹp hơn 240px — không bao giờ đẩy trang thành cuộn ngang trên điện thoại.
 */
export function PhotoThumbList({
  images,
  label,
  alt,
  openLabel,
  overlay,
  caption,
  className,
  itemClassName,
}: {
  images: string[];
  /** Tên nhóm cho người đọc màn hình, vd "Ảnh ghi chú". */
  label?: string | undefined;
  alt?: ((i: number) => string) | undefined;
  openLabel?: ((i: number) => string) | undefined;
  /** Lớp phủ riêng từng ô (nút ✕…). */
  overlay?: ((src: string, i: number) => ReactNode) | undefined;
  /** Dòng chữ dưới từng ô (người chụp, giờ chụp…). */
  caption?: ((src: string, i: number) => ReactNode) | undefined;
  className?: string | undefined;
  /** Lớp cho ô bọc từng ảnh (hiệu ứng hiện ra của ảnh vừa đính…). */
  itemClassName?: string | undefined;
}) {
  const [open, setOpen] = useState(-1);
  if (images.length === 0) return null;

  return (
    <>
      <div role="group" aria-label={label} className={cn("flex flex-wrap gap-2", className)}>
        {images.map((src, i) => (
          // `min-w-0`: ô được co về bề ngang khung chứa khi khung hẹp hơn
          // 240px — không có thì ô đòi đủ 240px và đẩy trang cuộn ngang.
          <div key={`${src}|${i}`} className={cn("min-w-0 max-w-full", itemClassName)}>
            <PhotoThumb
              src={src}
              alt={alt?.(i)}
              onOpen={() => setOpen(i)}
              openLabel={openLabel?.(i) ?? `Xem ảnh ${i + 1} phóng to`}
            >
              {overlay?.(src, i)}
            </PhotoThumb>
            {caption?.(src, i)}
          </div>
        ))}
      </div>
      <ImageLightbox
        urls={images.map((s) => photoUrl(s, "lg"))}
        thumbUrls={images.map((s) => photoUrl(s, "thumb"))}
        origHref={(i) => photoUrl(images[i] ?? "", "lg")}
        index={open}
        onIndex={setOpen}
        onClose={() => setOpen(-1)}
      />
    </>
  );
}
